import {
    AfterViewInit,
    Directive,
    ElementRef,
    inject,
    Input,
    input,
    InputSignal,
    effect,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { IqCssClassnames, IqCssClassNamesRule } from '../models';
import { IqStyleProcessorMigratorService } from '../state/services/iq-style-processor-migrator.service';
import { get, isEmpty, set, uniqueId } from 'lodash';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';

type RuleResponseTypes = 'CSS_CLASS_COMPONENT';
type SbCssClassDirectiveResponse = {
    sbCssClassComponentId: string;
    cssClasses: IqCssClassNamesRule;
    type: RuleResponseTypes;
    rules: { [key: string]: any; type: string }[];
};

@Directive({
    selector: '[iqStyleProcessorClassname]',
    standalone: true,
})
export class IqCssClassProcessorDirective
    implements AfterViewInit, OnDestroy, OnInit
{
    @Input()
    iqStyleProcessorClassname: IqCssClassnames = {};

    private _migrator: IqStyleProcessorMigratorService = inject(
        IqStyleProcessorMigratorService,
    );
    private _eventBroker: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private _registeredAction: Record<string, IqEventBusBrokerHandler> = {};
    private _cssDirectiveProcessorId: string = uniqueId(
        'sp-css-class-directive',
    );

    cssClassRules: InputSignal<IqCssClassNamesRule[]> = input([]);
    // cssClassRuleOperator: InputSignal<'AND' | 'OR' | 'NOT'> = input('AND');
    runRules: InputSignal<boolean> = input(true);

    elementRef: ElementRef = inject(ElementRef);

    constructor() {
        effect(() => {
            if (!this.runRules()) return;

            this._runClassesWithNoRules();
        });
    }

    ngOnInit() {
        if (!this.runRules()) return;

        this._runClassesWithNoRules();
    }
    private _runClassesWithNoRules() {
        const rules: IqCssClassNamesRule[] = !!this.cssClassRules()?.length
            ? this.cssClassRules()
            : this._migrator.mapToNewCssRules(
                  this.iqStyleProcessorClassname ?? {},
              );
        const noRuleCssClasses = rules.filter((rule) => isEmpty(rule.rules));

        this._addCssClasses(noRuleCssClasses);
    }

    ngAfterViewInit() {
        this._runClassesWithNoRules();
        this._checkRules();
        setTimeout(() => {
            this._runClassesWithNoRules();
            this._checkRules();
        });
    }

    ngOnDestroy() {
        this._eventBroker.unregisterMap(this._registeredAction ?? {});
    }

    private _checkRules() {
        const rules: IqCssClassNamesRule[] = !!this.cssClassRules()?.length
            ? this.cssClassRules()
            : this._migrator.mapToNewCssRules(
                  this.iqStyleProcessorClassname ?? {},
              );
        const hasRuleCssClasses = rules.filter((rule) => !isEmpty(rule.rules));

        if (!hasRuleCssClasses?.length) return;

        this._registeredAction = this._eventBroker.registerMap({
            'type:rules:evaluator:run:success': {
                callback: (eventInfo: SbCssClassDirectiveResponse) => {
                    if (
                        get(eventInfo, 'cssDirectiveProcessorId') !==
                        this._cssDirectiveProcessorId
                    )
                        return;

                    this._runSuccessRules(eventInfo);
                    // console.log('eventInfo', eventInfo);
                    // console.log('success');
                },
            },
            'type:rules:evaluator:run:failure': {
                callback: (eventInfo: SbCssClassDirectiveResponse) => {
                    if (
                        get(eventInfo, 'cssDirectiveProcessorId') !==
                        this._cssDirectiveProcessorId
                    )
                        return;

                    // console.log('failed');
                    this._runFailRules(eventInfo);
                },
            },
        });

        hasRuleCssClasses.forEach((rule: IqCssClassNamesRule) => {
            this._eventBroker.dispatch({
                name: 'type:rules:evaluator:run',
                payload: {
                    cssDirectiveProcessorId: this._cssDirectiveProcessorId,
                    type: 'CSS_CLASS_COMPONENT',
                    operator: rule.ruleOperator,
                    ...rule,
                },
            });
        });
    }

    private _addCssClasses(rules: IqCssClassNamesRule[]) {
        rules.forEach((rule: IqCssClassNamesRule) => {
            const { classNames, cssSelectors } = rule;

            cssSelectors.forEach((cssSelector: string) => {
                const elements = !cssSelector?.length
                    ? [this.elementRef.nativeElement]
                    : this.elementRef.nativeElement?.querySelectorAll(
                          cssSelector,
                      );

                elements.forEach((element: HTMLElement) => {
                    if (!element?.classList) return;
                    classNames.forEach((className: string) => {
                        element?.classList?.add(className);
                    });
                });
            });
        });
    }
    private _removeCssClasses(rules: IqCssClassNamesRule[]) {
        rules.forEach((rule: IqCssClassNamesRule) => {
            const { classNames, cssSelectors } = rule;

            cssSelectors.forEach((cssSelector: string) => {
                const elements = !cssSelector?.length
                    ? [this.elementRef.nativeElement]
                    : this.elementRef.nativeElement?.querySelectorAll(
                          cssSelector,
                      );

                elements.forEach((element: HTMLElement) => {
                    if (!element?.classList) return;
                    classNames.forEach((className: string) => {
                        element?.classList?.remove(className);
                    });
                });
            });
        });
    }

    private _runSuccessRules(eventInfo: SbCssClassDirectiveResponse) {
        if (
            get(eventInfo, 'cssDirectiveProcessorId') !==
            this._cssDirectiveProcessorId
        ) {
            return;
        }

        if (
            Object.keys(eventInfo).includes('cssSelectors') &&
            Object.keys(eventInfo).includes('classNames')
        ) {
            this._addCssClasses([
                {
                    classNames: (eventInfo as any)?.classNames as any,
                    cssSelectors: (eventInfo as any)?.cssSelectors ?? [],
                },
            ]);
        }
    }

    private _runFailRules(eventInfo: SbCssClassDirectiveResponse) {
        if (
            get(eventInfo, 'cssDirectiveProcessorId') !==
            this._cssDirectiveProcessorId
        )
            return;

            if (
                Object.keys(eventInfo).includes('cssSelectors') &&
                Object.keys(eventInfo).includes('classNames')
            ) {
              
                this._removeCssClasses([
                    {
                        classNames: (eventInfo as any)?.classNames as any,
                        cssSelectors: (eventInfo as any)?.cssSelectors ?? [],
                    },
                ]);
            }
    }
}
